import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import api from '../services/api';

const ConsignmentForm = ({ open, consignment, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [sales, setSales] = useState([]);

  const [formData, setFormData] = useState({
    vendor_id: '',
    item_id: '',
    consignment_date: new Date().toISOString().split('T')[0],
    agreed_price: '',
    commission_rate: '30',
    sale_id: '',
    notes: '',
    contract_terms: '',
  });

  useEffect(() => {
    fetchVendors();
    fetchItems();
    fetchSales();

    if (consignment) {
      setFormData({
        vendor_id: consignment.vendor_id || '',
        item_id: consignment.item_id || '',
        consignment_date: consignment.consignment_date
          ? consignment.consignment_date.split('T')[0]
          : new Date().toISOString().split('T')[0],
        agreed_price: consignment.agreed_price || '',
        commission_rate: consignment.commission_rate || '30',
        sale_id: consignment.sale_id || '',
        notes: consignment.notes || '',
        contract_terms: consignment.contract_terms || '',
      });
    }
  }, [consignment]);

  const fetchVendors = async () => {
    try {
      const response = await api.get('/api/admin/clients');
      setVendors(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await api.get('/api/inventory');
      setItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const fetchSales = async () => {
    try {
      const response = await api.get('/api/sales');
      setSales(response.data.sales || []);
    } catch (error) {
      console.error('Error fetching sales:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = {
        ...formData,
        vendor_id: parseInt(formData.vendor_id),
        item_id: parseInt(formData.item_id),
        agreed_price: parseFloat(formData.agreed_price),
        commission_rate: parseFloat(formData.commission_rate),
        sale_id: formData.sale_id ? parseInt(formData.sale_id) : null,
      };

      if (consignment) {
        await api.put(`/api/consignments/${consignment.id}`, data);
      } else {
        await api.post('/api/consignments', data);
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving consignment:', error);
      setError(error.response?.data?.error || 'Error saving consignment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{consignment ? 'Edit Consignment' : 'New Consignment'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Vendor</InputLabel>
                <Select
                  name="vendor_id"
                  value={formData.vendor_id}
                  onChange={handleChange}
                  label="Vendor"
                  disabled={!!consignment}
                >
                  {vendors.map((vendor) => (
                    <MenuItem key={vendor.id} value={vendor.id}>
                      {vendor.user?.first_name} {vendor.user?.last_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Item</InputLabel>
                <Select
                  name="item_id"
                  value={formData.item_id}
                  onChange={handleChange}
                  label="Item"
                  disabled={!!consignment}
                >
                  {items
                    .filter((item) => item.status === 'available' || item.id === formData.item_id)
                    .map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name} - ${item.price}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Consignment Date"
                name="consignment_date"
                type="date"
                value={formData.consignment_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Agreed Price"
                name="agreed_price"
                type="number"
                value={formData.agreed_price}
                onChange={handleChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ step: '0.01', min: '0' }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Commission Rate"
                name="commission_rate"
                type="number"
                value={formData.commission_rate}
                onChange={handleChange}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                inputProps={{ step: '0.01', min: '0', max: '100' }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Estate Sale (Optional)</InputLabel>
                <Select
                  name="sale_id"
                  value={formData.sale_id}
                  onChange={handleChange}
                  label="Estate Sale (Optional)"
                >
                  <MenuItem value="">None</MenuItem>
                  {sales.map((sale) => (
                    <MenuItem key={sale.id} value={sale.id}>
                      {sale.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Contract Terms"
                name="contract_terms"
                value={formData.contract_terms}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : consignment ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ConsignmentForm;
